import { useEffect, useState, useRef } from "react";
import { useAuth } from "./auth";
import { useTenant } from "./tenant";

export type RealtimeEvent = {
  id: string;
  type: string;
  ts: number;
  tenant_id: string;
  payload?: any;
};

const ORIGIN = ""; // same-origin
const FETCH_RECONNECT_BASE_MS = 2_000;
const FETCH_RECONNECT_MAX_MS = 30_000;

type RealtimeConnection =
  | { mode: "eventsource"; url: string }
  | { mode: "fetch"; url: string; headers: Record<string, string> };

export function buildRealtimeConnection({
  effectiveTenantId,
  token,
}: {
  effectiveTenantId: string | null;
  token: string | null;
}): RealtimeConnection | null {
  const tenantId = effectiveTenantId?.trim();
  if (!tenantId) return null;

  const bearer = token?.trim();
  if (bearer) {
    return {
      mode: "fetch",
      url: `${ORIGIN}/v1/events/stream`,
      headers: {
        Accept: "text/event-stream",
        Authorization: `Bearer ${bearer}`,
        "X-Tenant-Id": tenantId,
      },
    };
  }

  return {
    mode: "eventsource",
    url: `${ORIGIN}/v1/events/stream?tenant_id=${encodeURIComponent(tenantId)}`,
  };
}

export function parseRealtimeSseBlock(block: string): RealtimeEvent | null {
  const data = block
    .split(/\r?\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart())
    .join("\n");
  if (!data) return null;

  try {
    const event = JSON.parse(data) as RealtimeEvent;
    if (event.type === "heartbeat" || event.type === "hello") return null;
    return event;
  } catch {
    return null;
  }
}

export function realtimeReconnectDelay(attempt: number): number {
  const normalizedAttempt = Math.max(1, Math.floor(attempt));
  return Math.min(FETCH_RECONNECT_MAX_MS, FETCH_RECONNECT_BASE_MS * 2 ** (normalizedAttempt - 1));
}

export function shouldRetryRealtimeFetchStatus(status: number): boolean {
  return status !== 401 && status !== 403;
}

export function useRealtime(): { connected: boolean; events: RealtimeEvent[] } {
  const { token } = useAuth();
  const { effectiveTenantId } = useTenant();
  const [connected, setConnected] = useState(false);
  const [events, setEvents] = useState<RealtimeEvent[]>([]);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const connection = buildRealtimeConnection({ effectiveTenantId, token });
    if (!connection) return;

    let cancelled = false;
    const pushEvent = (event: RealtimeEvent | null) => {
      if (!event || cancelled) return;
      setEvents((prev) => [{ ...event, _seq: Date.now() + Math.random() } as RealtimeEvent, ...prev].slice(0, 50));
    };

    if (connection.mode === "eventsource") {
      const es = new EventSource(connection.url);
      esRef.current = es;
      es.onopen = () => setConnected(true);
      es.onerror = () => setConnected(false);
      const handleEvent = (e: MessageEvent) => pushEvent(parseRealtimeSseBlock(`data: ${e.data}`));
      es.addEventListener("agent_run", handleEvent);
      es.addEventListener("quality_check", handleEvent);
      es.addEventListener("event", handleEvent);
      es.onmessage = handleEvent;
      return () => {
        cancelled = true;
        es.close();
        esRef.current = null;
        setConnected(false);
      };
    }

    const controller = new AbortController();
    void (async () => {
      let attempts = 0;
      while (!cancelled) {
        try {
          const res = await fetch(connection.url, {
            headers: connection.headers,
            signal: controller.signal,
          });
          if (!res.ok || !res.body) {
            if (!shouldRetryRealtimeFetchStatus(res.status)) return;
            throw new Error(`realtime fetch ${res.status}`);
          }
          attempts = 0;
          if (!cancelled) setConnected(true);

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (!cancelled) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split(/\r?\n\r?\n/);
            buffer = blocks.pop() ?? "";
            blocks.forEach((block) => pushEvent(parseRealtimeSseBlock(block)));
          }
          if (buffer) pushEvent(parseRealtimeSseBlock(buffer));
        } catch (error) {
          if (cancelled || (error instanceof DOMException && error.name === "AbortError")) return;
        }

        if (!cancelled) {
          setConnected(false);
          attempts += 1;
          await new Promise((resolve) => window.setTimeout(resolve, realtimeReconnectDelay(attempts)));
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      setConnected(false);
    };
  }, [effectiveTenantId, token]);

  return { connected, events };
}
